import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { ApolloError } from '@apollo/client';
import { useCreateProductDeveloperSchemaMarkupMutation } from '../../../../../../graphql/mutation/product/productDeveloperSchemaMarkup/createProductDeveloperSchemaMarkupMutation';
import useNotification from '../../../../../components/_hook/useNotification';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import useForm from '../../../../../components/_hook/useForm';
import { useUpdateProductDeveloperSchemaMarkupMutation } from '../../../../../../graphql/mutation/product/productDeveloperSchemaMarkup/updateProductDeveloperSchemaMarkupMutation';
import NotFound from '../../../../../components/_error/NotFound';
import useRouter from '../../../../../components/_hook/useRouter';
import { useProductQuery } from '../../../../../../graphql/query/product/useProductQuery';
import InputTranslationTextField from '../../../../../components/_input/InputTranslation/InputTranslationTextField';
import Select, {
  IValue,
} from '../../../../../components/_select/_module/Select';
import PRODUCT, {
  IProductConstant,
} from '../../../../../components/constant/product';
import Context from '../../../../../context';

interface IProps {
  schemaMarkupId?: string;
  onSubmit?: () => void;
  onCancel: () => void;
}

export default function FormCreateUpdateProductDeveloperSchemaMarkup(
  props: IProps
) {
  interface IFormValue {
    status: boolean;
    name: string;
    content: {
      la: string;
      text: string;
    }[];
  }

  const { t } = useTranslation();
  const { schemaMarkupId, onSubmit, onCancel } = props;
  const { company } = useContext(Context);
  const { params } = useRouter();
  const { docID: productId } = params;
  const { notification } = useNotification();
  const { data, loading } = useProductQuery(
    `
			_id
			developer {
              schema_markup {
                id
                status
                name
                content {
                  la
                  text
                }
              }
            }
		`,
    {
      variables: {
        filter: {
          companyId: company._id,
          _id: productId as string,
        },
      },
      skip: !schemaMarkupId,
    }
  );

  function onCompleted() {
    notification.success(t('success'));
    if (onSubmit) {
      onSubmit();
    }
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    createProductDeveloperSchemaMarkupMutation,
    { loading: isCreatingProductDeveloperSchemaMarkup },
  ] = useCreateProductDeveloperSchemaMarkupMutation(
    `updatedProduct {
      _id
      developer {
        schema_markup {
          id
          status
          name
          content {
            la
            text
          }
        }
      }
    }`,
    {
      onCompleted,
      onError,
    }
  );
  const [
    updateProductDeveloperSchemaMarkupMutation,
    { loading: isUpdatingProductDeveloperSchemaMarkup },
  ] = useUpdateProductDeveloperSchemaMarkupMutation(
    `updatedProduct {
      _id
      developer {
        schema_markup {
          id
          status
          name
          content {
            la
            text
          }
        }
      }
    }`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (data && schemaMarkupId) {
      await updateProductDeveloperSchemaMarkupMutation({
        variables: {
          productId,
          developerSchemaMarkupId: schemaMarkupId,
          status: tempValues.status,
          name: tempValues.name,
          content: tempValues.content,
        },
      });
    } else {
      await createProductDeveloperSchemaMarkupMutation({
        variables: {
          productId,
          status: tempValues.status,
          name: tempValues.name,
          content: tempValues.content,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      name: '',
      content: [],
      status: true,
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.name) {
        newErrors.name = t('please_enter_the_correct__type__', {
          type: t('type'),
        });
      }

      if (!tempValues.content) {
        newErrors.content = t('please_enter_the_correct__type__', {
          type: t('content'),
        });
      }

      return newErrors;
    },
  });

  useEffect(() => {
    if (schemaMarkupId && !loading && data && data.products.items.length) {
      const product = data.products.items[0];
      const developer = product.developer || {};
      const schemaMarkups = developer.schema_markup || [];
      const schemaMarkup = schemaMarkups.find(
        (tempSchemaMarkup) => tempSchemaMarkup.id === schemaMarkupId
      );

      if (schemaMarkup) {
        setValues({
          name: schemaMarkup.name,
          content: schemaMarkup.content.map((tempContent) => ({
            text: tempContent.text,
            la: tempContent.la,
          })),
          status: schemaMarkup.status,
        });
      }
    }
  }, [loading, schemaMarkupId]);

  if (loading && schemaMarkupId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  if (schemaMarkupId && (!data || data.products.items.length === 0)) {
    return <NotFound />;
  }

  let schemaMarkup = null;

  if (schemaMarkupId && data && data.products.items.length) {
    const product = data.products.items[0];
    const developer = product.developer || {};
    const schemaMarkups = developer.schema_markup || [];
    schemaMarkup = schemaMarkups.find(
      (tempSchemaMarkup) => tempSchemaMarkup.id === schemaMarkupId
    );
  }

  return (
    <>
      <DialogTitle>
        {schemaMarkup ? (
          <>
            {t('update')} {schemaMarkup.name} {t('schema_markup')}
          </>
        ) : (
          <>
            {t('create')} {t('schema_markup')}
          </>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.status}
                    onChange={() => setValue('status', !values.status)}
                    color="primary"
                  />
                }
                label={t('publish')}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <Select<IValue>
              required
              isClearable
              options={Object.keys(PRODUCT.SCHEMA_MARKUP).map(
                (tempSchemaMarkup) => ({
                  label:
                    PRODUCT.SCHEMA_MARKUP[
                      tempSchemaMarkup as keyof IProductConstant['SCHEMA_MARKUP']
                    ],
                  value:
                    PRODUCT.SCHEMA_MARKUP[
                      tempSchemaMarkup as keyof IProductConstant['SCHEMA_MARKUP']
                    ],
                })
              )}
              label={t('type')}
              fullWidth
              menuPosition="fixed"
              value={values.name}
              onChange={(option) =>
                setValue('name', option ? option.value : '')
              }
              error={Boolean(errors.name)}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <InputTranslationTextField
              fullWidth
              required
              label={t('content')}
              values={values.content}
              onChange={(tempValues) => setValue('content', tempValues)}
              error={Boolean(errors.content)}
              helperText={errors.content}
            />
          </Grid>
          <Grid item xs={12}>
            {t('detail')}:&nbsp;
            <a
              href="https://schema.org/Product"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://schema.org/Product
            </a>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <ButtonSubmit
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              isSubmitting={
                isUpdatingProductDeveloperSchemaMarkup ||
                isCreatingProductDeveloperSchemaMarkup
              }
              label={t('submit')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
